import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import Button from '../_Core/Button';
import UserLogin from '../UserLogin/UserLogin';
import MyCaptcha from '../FormFromApiData/ReCaptcha';

interface IRegisterRole {
  Key: string;
  Label: string;
}

interface IRegisterState {
  Step: string;
  Message?: string;
  RedirectTo?: string;
  FirstName?: string;
  LastName?: string;
  Email?: string;
  PersonId?: string;
  EmailRestricted?: boolean;
  LoginWithEnabled?: boolean;
  AllowedEmailDomains?: string[];
  Roles?: IRegisterRole[];
}

/**
 * Replaces Register.aspx: collect name/email (or deep-link), then choose role → /app/user/new/...
 */
const RegisterPage = () => {
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<IRegisterState | null>(null);
  const [first, setFirst] = useState(query.get('first') || query.get('First') || '');
  const [last, setLast] = useState(query.get('last') || query.get('Last') || '');
  const [email, setEmail] = useState(query.get('email') || query.get('Email') || '');
  const [token, setToken] = useState(query.get('token') || '');
  const [saving, setSaving] = useState(false);

  const applyState = (s: IRegisterState) => {
    setState(s);
    if (s.FirstName) setFirst(s.FirstName);
    if (s.LastName) setLast(s.LastName);
    if (s.Email) setEmail(s.Email);
    if (s.Step === 'redirect' && s.RedirectTo) {
      window.location.href = s.RedirectTo;
    }
  };

  useEffect(() => {
    const payload = {
      first: query.get('first') || query.get('First') || '',
      last: query.get('last') || query.get('Last') || '',
      email: query.get('email') || query.get('Email') || '',
      token: query.get('token') || '',
      confirm: (query.get('confirm') || '').toLowerCase() === 'y' || query.get('confirm') === '1' || query.get('confirm') === 'true',
      stdReg: (query.get('stdReg') || '').toLowerCase() === 'y' || query.get('stdReg') === '1' || query.get('stdReg') === 'true',
      p: query.get('p') || query.get('P') || '',
    };

    store.server
      .postApi<ServerResponse<IRegisterState>>('../Register/State', payload)
      .then((x) => {
        if (x.Success && x.Value) {
          applyState(x.Value);
        } else {
          toast.error(x.Message || 'Unable to load registration.', { autoClose: false });
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Unable to load registration.', { autoClose: false });
      })
      .finally(() => setLoading(false));
  }, [query]);

  const start = () => {
    toast.dismiss();
    if (!first.trim() || !last.trim() || !email.trim()) {
      toast.warning('First name, last name, and email are required.');
      return;
    }
    let captchaToken = token;
    if (!captchaToken) {
      const w: any = window;
      captchaToken = w.x_reCaptcha || '';
    }

    setSaving(true);
    store.server
      .postApi<ServerResponse<IRegisterState>>('../Register/Start', {
        first: first.trim(),
        last: last.trim(),
        email: email.trim(),
        token: captchaToken,
      })
      .then((x) => {
        if (x.Success && x.Value) {
          applyState(x.Value);
        } else {
          toast.error(x.Message || 'Unable to start registration.', { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  const selectRole = (regType: string) => {
    setSaving(true);
    store.server
      .postApi<ServerResponse<IRegisterState>>('../Register/SelectRole', { regType })
      .then((x) => {
        if (x.Success && x.Value) {
          applyState(x.Value);
        } else {
          toast.error(x.Message || 'Unable to select registration type.', { autoClose: false });
        }
      })
      .finally(() => setSaving(false));
  };

  if (loading || (state?.Step === 'redirect' && state.RedirectTo)) {
    return (
      <div className="main-body-page grow text-center" data-testid="register-page" style={{ minHeight: 120 }}>
        <p>Loading registration...</p>
      </div>
    );
  }

  return (
    // minHeight so Playwright visibility checks work (Bootstrap float cols can collapse wrapper height to 0)
    <div className="main-body-page grow" data-testid="register-page" style={{ minHeight: 120, overflow: 'auto' }}>
      {state?.Step === 'login' && (
        <div data-testid="register-login">
          {state.Message && <div className="alert alert-info">{state.Message}</div>}
          <UserLogin />
        </div>
      )}

      {state?.Step === 'email-failed' && (
        <div className="alert alert-danger" data-testid="register-email-failed">
          <h3>Oops!</h3>
          <p>{state.Message}</p>
        </div>
      )}

      {state?.Step === 'base-info' && (
        <div data-testid="register-base-info" style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          <div style={{ flex: '1 1 320px', maxWidth: 640 }}>
            <h2>Register</h2>
            <div className="form-horizontal">
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor="reg-first">
                  First Name
                </label>
                <div className="col-sm-8">
                  <input
                    id="reg-first"
                    type="text"
                    className="form-control"
                    data-testid="register-first"
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor="reg-last">
                  Last Name
                </label>
                <div className="col-sm-8">
                  <input
                    id="reg-last"
                    type="text"
                    className="form-control"
                    data-testid="register-last"
                    value={last}
                    onChange={(e) => setLast(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="col-sm-4 control-label" htmlFor="reg-email">
                  Email
                </label>
                <div className="col-sm-8">
                  <input
                    id="reg-email"
                    type="email"
                    className="form-control"
                    data-testid="register-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {state.EmailRestricted && (
                    <div className="password-error" data-testid="register-email-restricted">
                      You must use an allowed email account.
                      <ul>
                        {(state.AllowedEmailDomains || []).map((d) => (
                          <li key={d}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              {StoreX.Settings?.ReCaptchaRequiredToRegister && (
                <div className="form-group">
                  <div className="col-sm-8 col-sm-offset-4">
                    <MyCaptcha
                      fieldChanged={(_key: string, v: string[]) => {
                        setToken(v?.[0] || '');
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <div className="col-sm-8 col-sm-offset-4">
                  <Button type="save" text={saving ? 'Working...' : 'Register'} onClick={start} disabled={saving} />
                </div>
              </div>
            </div>
          </div>
          {state.LoginWithEnabled && (
            <div style={{ flex: '0 1 240px' }} data-testid="register-oauth">
              <h3>Or Register Using</h3>
              <a href={StoreX.BuildUrlWithFair('../Auth/Login?providerName=Google&oathType=REGISTER')} className="btn btn-default btn-block">
                <i className="fa-brands fa-google"></i>&nbsp;Google
              </a>
              <a href={StoreX.BuildUrlWithFair('../Auth/Login?providerName=WindowsLive&oathType=REGISTER')} className="btn btn-default btn-block">
                <i className="fa-brands fa-windows"></i>&nbsp;Windows Live
              </a>
              <a href={StoreX.BuildUrlWithFair('../Auth/Login?providerName=Facebook&oathType=REGISTER')} className="btn btn-default btn-block">
                <i className="fa-brands fa-facebook"></i>&nbsp;Facebook
              </a>
            </div>
          )}
        </div>
      )}

      {state?.Step === 'select-role' && (
        <div data-testid="register-select-role" style={{ maxWidth: 420, margin: '0 auto' }}>
          <h2>Register as a?</h2>
          <h4>Select what you are registering as</h4>
          {(state.Roles || []).map((r) => (
            <button
              key={r.Key}
              type="button"
              className="btn-block btn btn-lg btn-default"
              data-testid={`register-role-${r.Key}`}
              disabled={saving}
              onClick={() => selectRole(r.Key)}
              style={{ marginBottom: 8 }}
            >
              {r.Label || r.Key}
            </button>
          ))}
          {(!state.Roles || state.Roles.length === 0) && (
            <div className="alert alert-warning">No registration types are currently open.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
